import type * as React from 'react';
import type { RepoBrowserTreeNode } from '../types/source-system';

interface RepoTreeExplorerProps {
  roots: RepoBrowserTreeNode[];
  expandedNodeIds: Set<string>;
  selectedNodeId: string | null;
  onSelectNode: (node: RepoBrowserTreeNode) => void;
  onToggleNode: (nodeId: string) => void;
}

export function RepoTreeExplorer({
  roots,
  expandedNodeIds,
  selectedNodeId,
  onSelectNode,
  onToggleNode,
}: RepoTreeExplorerProps) {
  return (
    <div className="ss-browser-tree" role="tree" aria-label="Repository contents">
      {roots.map((root) => (
        <RepoTreeNodeRow
          key={root.id}
          node={root}
          depth={0}
          expandedNodeIds={expandedNodeIds}
          selectedNodeId={selectedNodeId}
          onSelectNode={onSelectNode}
          onToggleNode={onToggleNode}
        />
      ))}
    </div>
  );
}

interface RepoTreeNodeRowProps {
  node: RepoBrowserTreeNode;
  depth: number;
  expandedNodeIds: Set<string>;
  selectedNodeId: string | null;
  onSelectNode: (node: RepoBrowserTreeNode) => void;
  onToggleNode: (nodeId: string) => void;
}

function RepoTreeNodeRow({
  node,
  depth,
  expandedNodeIds,
  selectedNodeId,
  onSelectNode,
  onToggleNode,
}: RepoTreeNodeRowProps) {
  const isExpanded = expandedNodeIds.has(node.id);
  const isSelected = selectedNodeId === node.id;
  const canExpand = node.kind !== 'file' && node.children.length > 0;

  return (
    <div className="ss-browser-tree__branch">
      <div
        className={`ss-browser-tree__row${isSelected ? ' ss-browser-tree__row--selected' : ''}`}
        style={{ '--ss-tree-depth': `${depth}` } as React.CSSProperties}
      >
        <button
          type="button"
          className="ss-browser-tree__toggle"
          onClick={() => canExpand && onToggleNode(node.id)}
          disabled={!canExpand}
          aria-label={canExpand ? `${isExpanded ? 'Collapse' : 'Expand'} ${node.name}` : undefined}
        >
          {canExpand && (
            <svg
              className="ss-browser-tree__chevron"
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              aria-hidden="true"
              data-expanded={isExpanded || undefined}
            >
              <path
                d="M4 2l4 4-4 4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </button>

        <button
          type="button"
          role="treeitem"
          aria-expanded={canExpand ? isExpanded : undefined}
          className={`ss-browser-tree__item ss-browser-tree__item--${node.kind}`}
          onClick={() => onSelectNode(node)}
        >
          <span className="ss-browser-tree__icon" aria-hidden="true">
            {iconForNode(node.kind, isExpanded)}
          </span>
          <span className="ss-browser-tree__label">{node.name}</span>
        </button>
      </div>

      {canExpand && isExpanded && (
        <div role="group" className="ss-browser-tree__children">
          {node.children.map((child) => (
            <RepoTreeNodeRow
              key={child.id}
              node={child}
              depth={depth + 1}
              expandedNodeIds={expandedNodeIds}
              selectedNodeId={selectedNodeId}
              onSelectNode={onSelectNode}
              onToggleNode={onToggleNode}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function iconForNode(kind: RepoBrowserTreeNode['kind'], isExpanded: boolean): string {
  switch (kind) {
    case 'repo':
      return '◫';
    case 'directory':
      return isExpanded ? '▣' : '▢';
    case 'file':
      return '≡';
    default:
      return '•';
  }
}
